import { useEffect, useMemo, useState } from "react";
import { animate } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FadeUp, Chapter, RevealLines, EASE } from "./motion";
import { useLead } from "@/App";

const BHK_BASE = { "1 BHK": 450000, "2 BHK": 750000, "3 BHK": 1100000, "4 BHK": 1600000, "5 BHK": 2200000 };
const TIERS = { Essential: 0.85, Premium: 1.15, Luxury: 1.6 };
const SCOPES = { "Full Home": 1, "Kitchen + Wardrobes": 0.45, "Single Room": 0.18 };

const fmt = (n) => {
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${Math.round(n / 1000)}K`;
};

function AnimatedNumber({ value }) {
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const controls = animate(display, value, {
      duration: 0.9,
      ease: EASE,
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <>{fmt(display)}</>;
}

const Pill = ({ label, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
      active ? "bg-[#1B1B1B] text-[#F7F5F2]" : "border hairline text-[#2C2C2C]/60 hover:border-[#B68D40] hover:text-[#B68D40]"
    }`}
  >
    {label}
  </button>
);

export default function Calculator() {
  const { openConsult } = useLead();
  const [bhk, setBhk] = useState("3 BHK");
  const [tier, setTier] = useState("Premium");
  const [scope, setScope] = useState("Full Home");

  const [low, high] = useMemo(() => {
    const base = BHK_BASE[bhk] * TIERS[tier] * SCOPES[scope];
    return [base * 0.88, base * 1.12];
  }, [bhk, tier, scope]);

  return (
    <section id="calculator" data-testid="calculator-section" className="bg-[#F7F5F2] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Chapter num="10" title="Investment Guide" />
        <RevealLines
          className="mt-8 font-display text-4xl tracking-tight text-[#1B1B1B] md:text-6xl"
          lines={[<>Know your estimate</>, <>in <span className="italic text-[#B68D40]">ten seconds.</span></>]}
        />

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr]">
          <FadeUp className="rounded-2xl border hairline bg-white p-8 md:p-12">
            <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#2C2C2C]/50">Your Home</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.keys(BHK_BASE).map((b) => (
                <Pill key={b} label={b} active={bhk === b} onClick={() => setBhk(b)} testid={`calc-bhk-${b.replace(/\s/g, "").toLowerCase()}`} />
              ))}
            </div>

            <p className="mt-10 font-num text-[10px] uppercase tracking-[0.3em] text-[#2C2C2C]/50">Scope</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.keys(SCOPES).map((s) => (
                <Pill key={s} label={s} active={scope === s} onClick={() => setScope(s)} testid={`calc-scope-${s.toLowerCase().replace(/[\s+]/g, "-")}`} />
              ))}
            </div>

            <p className="mt-10 font-num text-[10px] uppercase tracking-[0.3em] text-[#2C2C2C]/50">Finish Tier</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.keys(TIERS).map((t) => (
                <Pill key={t} label={t} active={tier === t} onClick={() => setTier(t)} testid={`calc-tier-${t.toLowerCase()}`} />
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="grain relative flex flex-col justify-between overflow-hidden rounded-2xl bg-[#1B1B1B] p-8 md:p-12">
            <div>
              <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#B68D40]">Estimated Investment</p>
              <p data-testid="calc-estimate" className="mt-6 font-num text-5xl font-semibold tracking-tight text-[#F7F5F2] md:text-6xl">
                <AnimatedNumber value={low} />
                <span className="mx-3 text-[#B68D40]">—</span>
                <AnimatedNumber value={high} />
              </p>
              <p className="mt-6 text-sm leading-relaxed text-[#F7F5F2]/60">
                Indicative range for a {bhk} · {scope.toLowerCase()} · {tier.toLowerCase()} finishes, based on current Bengaluru project pricing. Your exact quote follows a free site visit.
              </p>
            </div>
            <button
              onClick={() => openConsult("calculator")}
              data-testid="calc-book-session-btn"
              className="group mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-[#B68D40] py-4 text-sm font-semibold uppercase tracking-widest text-[#1B1B1B] transition-colors duration-300 hover:bg-[#F7F5F2]"
            >
              Book Free Design Session
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
